// components/ui/error-modal.tsx
import { createContext, useContext, useState } from "react";
import { createPortal } from "react-dom";

type ErrorModalData = { 
  title: string; 
  description: string;
  onConfirm?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
};
type ErrorModalContextType = { showError: (data: ErrorModalData) => void };

const ErrorModalContext = createContext<ErrorModalContextType | null>(null);

export const useErrorModal = () => {
  const ctx = useContext(ErrorModalContext);
  if (!ctx) throw new Error("useErrorModal must be used within <ErrorModalProvider>");
  return ctx;
};

export const ErrorModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [modal, setModal] = useState<ErrorModalData | null>(null);

  const showError = (data: ErrorModalData) => setModal(data);
  const onClose = () => setModal(null);

  return (
    <ErrorModalContext.Provider value={{ showError }}>
      {children}

      {!!modal &&
        createPortal(
          <>
            {/* Overlay */}
            <div
              onClick={onClose}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 99998,
                backgroundColor: "rgba(0, 0, 0, 0.2)",
                backdropFilter: "blur(4px)",
              }}
            />

            {/* Modal */}
            <div
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                zIndex: 99999,
                transform: "translate(-50%, -50%)",
                width: "540px",
                minWidth: "540px",
                maxWidth: "540px",
                backgroundColor: "white",
                borderRadius: "16px",
                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
                padding: "32px",
              }}
            >
              {/* Title */}
              <h2
                style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  textAlign: "center",
                  color: "#111827",
                  margin: 0,
                }}
              >
                {modal.title}
              </h2>

              {/* Description */}
              <p
                style={{
                  fontSize: "14px",
                  textAlign: "center",
                  color: "#6b7280",
                  marginTop: "8px",
                  lineHeight: 1.6,
                  marginBottom: 0,
                }}
              >
                {modal.description}
              </p>

              {/* Footer */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "24px",
                  gap: "12px",
                }}
              >
                <button
                  onClick={onClose}
                  style={{
                    minWidth: "100px",
                    padding: "8px 16px",
                    fontSize: "14px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    backgroundColor: "white",
                    color: "#374151",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "white";
                  }}
                >
                  {modal.cancelLabel || "Close"}
                </button>
                {modal.onConfirm && (
                  <button
                    onClick={() => {
                      modal.onConfirm!();
                      onClose();
                    }}
                    style={{
                      minWidth: "100px",
                      padding: "8px 16px",
                      fontSize: "14px",
                      border: "none",
                      borderRadius: "6px",
                      backgroundColor: "#FBD332",
                      color: "#111827",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#e5bc24";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#FBD332";
                    }}
                  >
                    {modal.confirmLabel || "Proceed"}
                  </button>
                )}
              </div>
            </div>
          </>,
          document.body
        )}
    </ErrorModalContext.Provider>
  );
};
